import React, { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Upload, FileText, CheckCircle, AlertCircle, X, Plus, Trash2, FileSpreadsheet, Landmark } from 'lucide-react';
import { MoujaInfo } from '../types';
export const UploadScreen: React.FC = () => {
  const {
    user
  } = useAuth();
  const location = useLocation();
  const projnapanState = location.state as {
    fromProjnapan?: boolean;
    projnapanId?: string;
    district?: string;
    upazila?: string;
    smarakNo?: string;
  } | null;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submissionType, setSubmissionType] = useState<'new_khatian' | 'khatian_correction'>('new_khatian');
  const isUnicode = user?.role === 'zso';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emptyMoujaRow: MoujaInfo = {
    district: '',
    upazila: '',
    mouja: '',
    jlNumber: '',
    khatianStartNo: '',
    khatianEndNo: '',
    totalKhatian: '',
    totalSheet: '',
    remarks: ''
  };
  const [moujaDetails, setMoujaDetails] = useState<MoujaInfo[]>([{
    ...emptyMoujaRow
  }]);
  const [files, setFiles] = useState({
    khatianData: null as File | null,
    gadgetCopy: null as File | null,
    handoverLetter: null as File | null
  });
  const handleMoujaChange = (index: number, field: keyof MoujaInfo, value: string) => {
    const updatedDetails = [...moujaDetails];
    updatedDetails[index][field] = value;
    // Auto-calculate total khatian if start and end are numbers
    if (field === 'khatianStartNo' || field === 'khatianEndNo') {
      const start = parseInt(updatedDetails[index].khatianStartNo);
      const end = parseInt(updatedDetails[index].khatianEndNo);
      if (!isNaN(start) && !isNaN(end) && end >= start) {
        updatedDetails[index].totalKhatian = (end - start + 1).toString();
      }
    }
    setMoujaDetails(updatedDetails);
  };
  const addMoujaRow = () => {
    setMoujaDetails([...moujaDetails, {
      ...emptyMoujaRow
    }]);
  };
  const removeMoujaRow = (index: number) => {
    if (moujaDetails.length > 1) {
      const updatedDetails = [...moujaDetails];
      updatedDetails.splice(index, 1);
      setMoujaDetails(updatedDetails);
    }
  };
  const handleImportXls = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // Simulate reading XLS and populating rows
      setTimeout(() => {
        setMoujaDetails([{
          district: 'Dhaka',
          upazila: 'Savar',
          mouja: 'Aminbazar',
          jlNumber: '45',
          khatianStartNo: '1',
          khatianEndNo: '500',
          totalKhatian: '500',
          totalSheet: '50',
          remarks: 'Imported'
        }, {
          district: 'Dhaka',
          upazila: 'Savar',
          mouja: 'Bhakurta',
          jlNumber: '46',
          khatianStartNo: '501',
          khatianEndNo: '1200',
          totalKhatian: '700',
          totalSheet: '70',
          remarks: 'Imported'
        }]);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }, 500);
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: keyof typeof files) => {
    if (e.target.files && e.target.files[0]) {
      setFiles({
        ...files,
        [type]: e.target.files[0]
      });
    }
  };
  const removeFile = (type: keyof typeof files) => {
    setFiles({
      ...files,
      [type]: null
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccess(true);
      // Reset form
      setMoujaDetails([{
        ...emptyMoujaRow
      }]);
      setFiles({
        khatianData: null,
        gadgetCopy: null,
        handoverLetter: null
      });
      setTimeout(() => setShowSuccess(false), 5000);
    }, 1500);
  };
  const FileUploadZone = ({
    title,
    type,
    accept,
    hint
  }: {
    title: string;
    type: keyof typeof files;
    accept: string;
    hint: string;
  }) => <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 transition-colors relative" data-id="element-1544">
      {files[type] ? <div className="flex flex-col items-center w-full" data-id="element-1545">
          <div className="flex items-center justify-between w-full bg-white p-3 rounded border border-slate-200 shadow-sm" data-id="element-1546">
            <div className="flex items-center" data-id="element-1547">
              <FileText className="text-teal-600 mr-3" size={24} data-id="element-1548" />
              <div className="flex flex-col overflow-hidden" data-id="element-1549">
                <span className="text-sm font-medium text-slate-800 truncate max-w-[200px]" data-id="element-1550">
                  {files[type]?.name}
                </span>
                <span className="text-xs text-slate-500" data-id="element-1551">
                  {(files[type]?.size! / 1024).toFixed(1)} KB
                </span>
              </div>
            </div>
            <button type="button" onClick={() => removeFile(type)} className="text-slate-400 hover:text-red-500 p-1" data-id="element-1552">
              <X size={18} data-id="element-1553" />
            </button>
          </div>
        </div> : <>
          <Upload className="text-slate-400 mb-3" size={32} data-id="element-1554" />
          <p className="text-sm font-medium text-slate-700 mb-1" data-id="element-1555">{title}</p>
          <p className="text-xs text-slate-500 mb-4" data-id="element-1556">{hint}</p>
          <label className="btn-secondary text-sm cursor-pointer" data-id="element-1557">
            ফাইল নির্বাচন
            <input type="file" className="hidden" accept={accept} onChange={e => handleFileChange(e, type)} data-id="element-1558" />
          </label>
        </>}
    </div>;
  return <div className="max-w-5xl mx-auto" data-id="element-1559">
      <div className="mb-6 flex justify-between items-end" data-id="element-1560">
        <div data-id="element-1561">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-1562">তথ্য জমাদান</h1>
          <p className="text-slate-500" data-id="element-1563">
            খতিয়ান রেকর্ড এবং সহায়ক নথি আপলোড করুন
          </p>
        </div>
        <div className="bg-blue-50 text-blue-700 px-3 py-1.5 rounded-md text-sm font-medium border border-blue-200 flex items-center" data-id="element-1564">
          <AlertCircle size={16} className="mr-2" data-id="element-1565" />
          ফরম্যাট:{' '}
          {isUnicode ? 'ইউনিকোড (CSV/Excel)' : 'নন-ইউনিকোড (বিজয়/লিগ্যাসি)'}
        </div>
      </div>

      {showSuccess && <div className="mb-6 bg-teal-50 border border-teal-200 text-teal-800 px-4 py-3 rounded-md flex items-center" data-id="element-1566">
          <CheckCircle className="mr-3 text-teal-500" size={20} data-id="element-1567" />
          <div data-id="element-1568">
            <p className="font-medium" data-id="element-1569">জমাদান সফল</p>
            <p className="text-sm text-teal-700" data-id="element-1570">
              আপনার ডেটা যাচাইয়ের জন্য সেটেলমেন্ট প্রেস অফিসারের কাছে পাঠানো
              হয়েছে।
            </p>
          </div>
        </div>}

      {projnapanState?.fromProjnapan && <div className="mb-6 bg-indigo-50 border border-indigo-200 text-indigo-800 px-4 py-3 rounded-md flex items-center" data-id="element-1571">
          <Landmark className="mr-3 text-indigo-500 shrink-0" size={20} data-id="element-1572" />
          <div data-id="element-1573">
            <p className="font-medium" data-id="element-1574">
              প্রজ্ঞাপন ভিত্তিক সাবমিশন — {projnapanState.projnapanId}
            </p>
            <p className="text-sm text-indigo-600" data-id="element-1575">
              {projnapanState.district}, {projnapanState.upazila}
              {projnapanState.smarakNo && <span className="ml-2" data-id="element-1576">
                  • স্মারক নং: {projnapanState.smarakNo}
                </span>}
            </p>
          </div>
        </div>}

      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1577">
        <div className="card p-6" data-id="element-1578">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 border-b border-slate-100 pb-2" data-id="element-1579">
            জমাদানের ধরন
          </h2>
          <div className="flex space-x-6" data-id="element-1580">
            <label className={`flex items-center space-x-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-colors ${submissionType === 'new_khatian' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`} data-id="element-1581">
              <input type="radio" name="submissionType" value="new_khatian" checked={submissionType === 'new_khatian'} onChange={() => setSubmissionType('new_khatian')} className="text-teal-600 focus:ring-teal-500" data-id="element-1582" />
              <div data-id="element-1583">
                <p className="text-sm font-medium text-slate-800" data-id="element-1584">
                  নতুন খতিয়ান
                </p>
                <p className="text-xs text-slate-500" data-id="element-1585">
                  নতুন খতিয়ান রেকর্ড জমা দিন
                </p>
              </div>
            </label>
            <label className={`flex items-center space-x-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-colors ${submissionType === 'khatian_correction' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`} data-id="element-1586">
              <input type="radio" name="submissionType" value="khatian_correction" checked={submissionType === 'khatian_correction'} onChange={() => setSubmissionType('khatian_correction')} className="text-amber-600 focus:ring-amber-500" data-id="element-1587" />
              <div data-id="element-1588">
                <p className="text-sm font-medium text-slate-800" data-id="element-1589">
                  খতিয়ান সংশোধন
                </p>
                <p className="text-xs text-slate-500" data-id="element-1590">
                  বিদ্যমান খতিয়ানের সংশোধন জমা দিন
                </p>
              </div>
            </label>
          </div>
        </div>

        <div className="card p-0 overflow-hidden" data-id="element-1591">
          <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-1592">
            <h2 className="text-lg font-semibold text-slate-800" data-id="element-1593">
              মৌজা ও খতিয়ান তথ্য
            </h2>
            <div data-id="element-1594">
              <input type="file" accept=".xls,.xlsx" className="hidden" ref={fileInputRef} onChange={handleImportXls} data-id="element-1595" />
              <button type="button" onClick={() => fileInputRef.current?.click()} className="btn-secondary text-sm flex items-center py-1.5 px-3" data-id="element-1596">
                <FileSpreadsheet size={16} className="mr-2 text-green-600" data-id="element-1597" />
                এক্সেল থেকে আমদানি
              </button>
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-1598">
            <table className="min-w-full divide-y divide-slate-200" data-id="element-1599">
              <thead className="bg-slate-100" data-id="element-1600">
                <tr data-id="element-1601">
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-1602">
                    জেলা
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-1603">
                    উপজেলা
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-1604">
                    মৌজা
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-1605">
                    জে.এল. নং
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-1606">
                    শুরু নং
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-1607">
                    শেষ নং
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-1608">
                    মোট খতিয়ান
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-1609">
                    মোট শীট
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-1610">
                    মন্তব্য
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase" data-id="element-1611">
                    কার্যক্রম
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200" data-id="element-1612">
                {moujaDetails.map((row, index) => <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-id="element-1613">
                    <td className="px-2 py-2" data-id="element-1614">
                      <select required value={row.district} onChange={e => handleMoujaChange(index, 'district', e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" data-id="element-1615">
                        <option value="" data-id="element-1616">নির্বাচন করুন</option>
                        <option value="Dhaka" data-id="element-1617">ঢাকা</option>
                        <option value="Gazipur" data-id="element-1618">গাজীপুর</option>
                        <option value="Narayanganj" data-id="element-1619">নারায়ণগঞ্জ</option>
                        <option value="Chittagong" data-id="element-1620">চট্টগ্রাম</option>
                      </select>
                    </td>
                    <td className="px-2 py-2" data-id="element-1621">
                      <select required value={row.upazila} onChange={e => handleMoujaChange(index, 'upazila', e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" data-id="element-1622">
                        <option value="" data-id="element-1623">নির্বাচন করুন</option>
                        <option value="Savar" data-id="element-1624">সাভার</option>
                        <option value="Tongi" data-id="element-1625">টঙ্গী</option>
                        <option value="Rupganj" data-id="element-1626">রূপগঞ্জ</option>
                        <option value="Hathazari" data-id="element-1627">হাটহাজারী</option>
                      </select>
                    </td>
                    <td className="px-2 py-2" data-id="element-1628">
                      <input type="text" required value={row.mouja} onChange={e => handleMoujaChange(index, 'mouja', e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" placeholder="মৌজা" data-id="element-1629" />
                    </td>
                    <td className="px-2 py-2" data-id="element-1630">
                      <input type="text" required value={row.jlNumber} onChange={e => handleMoujaChange(index, 'jlNumber', e.target.value)} className="w-20 border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" placeholder="জে.এল." data-id="element-1631" />
                    </td>
                    <td className="px-2 py-2" data-id="element-1632">
                      <input type="text" required value={row.khatianStartNo} onChange={e => handleMoujaChange(index, 'khatianStartNo', e.target.value)} className="w-20 border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" placeholder="শুরু" data-id="element-1633" />
                    </td>
                    <td className="px-2 py-2" data-id="element-1634">
                      <input type="text" required value={row.khatianEndNo} onChange={e => handleMoujaChange(index, 'khatianEndNo', e.target.value)} className="w-20 border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" placeholder="শেষ" data-id="element-1635" />
                    </td>
                    <td className="px-2 py-2" data-id="element-1636">
                      <input type="number" required value={row.totalKhatian} onChange={e => handleMoujaChange(index, 'totalKhatian', e.target.value)} className="w-24 border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" placeholder="মোট" data-id="element-1637" />
                    </td>
                    <td className="px-2 py-2" data-id="element-1638">
                      <input type="number" required value={row.totalSheet} onChange={e => handleMoujaChange(index, 'totalSheet', e.target.value)} className="w-24 border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" placeholder="শীট" data-id="element-1639" />
                    </td>
                    <td className="px-2 py-2" data-id="element-1640">
                      <input type="text" value={row.remarks} onChange={e => handleMoujaChange(index, 'remarks', e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" placeholder="মন্তব্য" data-id="element-1641" />
                    </td>
                    <td className="px-2 py-2 text-center" data-id="element-1642">
                      <button type="button" onClick={() => removeMoujaRow(index)} disabled={moujaDetails.length === 1} className={`p-1.5 rounded ${moujaDetails.length === 1 ? 'text-slate-300 cursor-not-allowed' : 'text-red-500 hover:bg-red-50'}`} data-id="element-1643">
                        <Trash2 size={16} data-id="element-1644" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-t border-slate-200 bg-slate-50" data-id="element-1645">
            <button type="button" onClick={addMoujaRow} className="text-sm font-medium text-teal-600 hover:text-teal-800 flex items-center" data-id="element-1646">
              <Plus size={16} className="mr-1" data-id="element-1647" /> আরও সারি যোগ করুন
            </button>
          </div>
        </div>

        <div className="card p-6" data-id="element-1648">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 border-b border-slate-100 pb-2" data-id="element-1649">
            নথি আপলোড
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1650">
            <FileUploadZone title="খতিয়ান ডেটা ফাইল" type="khatianData" accept=".dat,.csv,.xlsx" hint={isUnicode ? 'CSV/Excel ফাইল আপলোড করুন' : '.mdf/.ldf ফাইল আপলোড করুন'} data-id="element-1651" />
            <FileUploadZone title="গেজেট কপি/কেস কপি" type="gadgetCopy" accept=".pdf" hint="স্ক্যান করা PDF আপলোড করুন" data-id="element-1652" />
            <FileUploadZone title="হস্তান্তর পত্র/সংশোধন পত্র" type="handoverLetter" accept=".pdf" hint="স্বাক্ষরিত PDF আপলোড করুন" data-id="element-1653" />
          </div>
        </div>

        <div className="flex justify-end space-x-4" data-id="element-1654">
          <button type="button" className="btn-secondary" data-id="element-1655">
            বাতিল
          </button>
          <button type="submit" className="btn-primary flex items-center" disabled={isSubmitting || !files.khatianData || !files.gadgetCopy || !files.handoverLetter} data-id="element-1656">
            {isSubmitting ? <>প্রক্রিয়াকরণ হচ্ছে...</> : <>
                <Upload size={18} className="mr-2" data-id="element-1657" />
                যাচাইয়ের জন্য জমা দিন
              </>}
          </button>
        </div>
      </form>
    </div>;
};